// Every Fixlr module, grouped into 5 categories. Flagships get full deep-dive pages at /product/:slug.
import {
  ClipboardList, Siren, FileText, KanbanSquare, CalendarDays, Repeat, Building2,
  Smartphone, MapPin, UserCircle2, Route, MessageCircle,
  Store, Network, ShieldCheck,
  Receipt, Boxes, FileSignature, BarChart3, ShieldAlert, Eye, Palette, Plug, FolderOpen,
  Sparkles, Wand2, LineChart,
  Truck, Workflow,
  Inbox, ScanLine, HardHat, FileWarning,
  Map, Landmark, Library,
} from "lucide-react";
import { DEEP, SHOTS } from "@/lib/modules-deep";

export const CATEGORIES = [
  {
    slug: "core-ops",
    icon: Workflow,
    name: "Core Ops",
    kicker: "The engine room",
    headline: "Every moving part of maintenance ops, on one thread.",
    desc: "Jobs, emergencies, quotes, projects, scheduling and contracts share one record, one audit trail and one assign picker — whether the work goes to your crew or a supplier.",
    color: "lime",
  },
  {
    slug: "portals",
    icon: Smartphone,
    name: "Portals",
    kicker: "Everyone on their own phone",
    headline: "Operatives, clients and the office — each with the screen they need.",
    desc: "A PWA for your team, a branded portal for clients, live maps and WhatsApp for everything in between. No app-store downloads, no training days.",
    color: "blue",
  },
  {
    slug: "supplier-lane",
    icon: Truck,
    name: "Supplier Lane",
    kicker: "Your external network, managed",
    headline: "Manage your whole supplier network from one screen.",
    desc: "Bulk-invite hundreds of contractors, let them quote and complete from their own portal, and never assign a job to a lapsed certificate again.",
    color: "lime",
  },
  {
    slug: "management",
    icon: BarChart3,
    name: "Management",
    kicker: "Money, assets, compliance",
    headline: "The grown-up layer underneath the operations.",
    desc: "Invoicing with Stripe pay-links, an asset register with QR stickers, digital job sheets, live dashboards, a single compliance hub and full white-label multi-tenancy.",
    color: "blue",
  },
  {
    slug: "intelligence",
    icon: Sparkles,
    name: "Intelligence",
    kicker: "Premium differentiator",
    headline: "Not a chatbot. A decision co-pilot.",
    desc: "AI that ranks the right supplier for every job with its reasoning shown, drafts the paperwork you'd rather not write, and surfaces what needs attention before you ask.",
    color: "lime",
  },
];

const LIVE = "https://app.fixlr.app";

const BASE_MODULES = [
  // ---------------- CORE OPS ----------------
  {
    slug: "jobs",
    category: "core-ops",
    flagship: true,
    icon: ClipboardList,
    name: "Jobs & Work Orders",
    kicker: "Operations core",
    headline: "Every job, one thread.",
    summary: "From request to completion — schedule, assign, track and close every work order with a full audit trail. Built for teams running hundreds of concurrent jobs without losing a single detail.",
    bullets: [
      "Create jobs from scratch or promote a quote in one click",
      "Assign to in-house operatives or external suppliers with one picker",
      "Auto-attach job sheets from your template library",
      "Line-item costings that flow straight into invoices",
      "Priority flags, SLA timers and push alerts keep teams honest",
    ],
    mockup: "jobs",
    liveUrl: LIVE,
    stat: { value: "<2m", label: "from job creation to operative push" },
    stats: [
      { value: "<2m", label: "creation → push" },
      { value: "100%", label: "of actions audit-logged" },
      { value: "1", label: "picker for crew + suppliers" },
    ],
    pains: [
      { title: "Two systems for two workforces", desc: "One tool for your own engineers, a spreadsheet for contractors, and nobody knows who's on what." },
      { title: "Jobs marked done that never were", desc: "No photos, no sign-off, no arrival time — just a status someone changed on a Friday." },
      { title: "Costs that never reach the invoice", desc: "Parts and hours captured on paper, re-typed weeks later, margin quietly leaking." },
      { title: "Priority is a phone call", desc: "Urgent work depends on who shouted loudest, not an SLA timer anyone can see." },
    ],
    deepDives: [
      { kicker: "Deep dive", title: "One picker. Suppliers or your own team.", body: "You never have to decide between 'managing vendors' or 'managing your team' — Fixlr handles both paths through the same job record. Pick an operative, a supplier, or let AI Supplier Matching suggest one.", mockup: "assign" },
      { kicker: "How it works", title: "Costings, line by line.", body: "Quotes roll into jobs and become draft invoice line items you can edit before billing. Markup A (your margin) and Markup B (client surcharge) are configurable per quote, with server-recomputed totals.", mockup: "costlines" },
    ],
    steps: [
      { title: "Raise", desc: "From a client request, a phone call, a QR scan on an asset or a promoted quote." },
      { title: "Assign", desc: "One picker for operatives and suppliers, with SLA, priority and job sheets attached automatically." },
      { title: "Deliver", desc: "Operative marks arrived, completes the sheet, uploads photos, logs time — from the van." },
      { title: "Close & bill", desc: "Costings become invoice lines. Client gets a branded PDF with a Stripe pay-link." },
    ],
    faqs: [
      { q: "Can a job go to both an in-house operative and a supplier?", a: "Yes — multi-assignee jobs are supported, and each party sees only their own scope in their portal." },
      { q: "Do suppliers need a licence?", a: "No. Suppliers use the free Supplier Portal; they never count towards your seats." },
      { q: "Can we import our existing jobs?", a: "CSV import for jobs, clients, properties and suppliers is included, with a guided mapping step." },
      { q: "What's the audit trail?", a: "Every status change, assignment, note, photo and cost edit is timestamped with the actor and visible on the job." },
    ],
    quote: { text: "We killed three tools the first week. Our SLA hit-rate went from 71% to 94% in two months.", name: "Chris H.", role: "Head of Operations, Belmont Estates" },
    related: ["quotes", "scheduling", "invoicing", "job-sheets"],
  },
  {
    slug: "emergency",
    category: "core-ops",
    flagship: true,
    icon: Siren,
    name: "Emergency Response",
    kicker: "Premium differentiator",
    headline: "Every minute matters. We track them all.",
    summary: "A parallel track for out-of-hours, urgent and safety-critical jobs with hard SLAs, auto-escalation and a red-alert channel in Slack/Teams. This is the reason large accounts pick Fixlr.",
    bullets: [
      "Separate emergency pipeline with red-flag styling across the platform",
      "Built-in SLA watcher and escalation scheduler",
      "Auto-notify on-call operatives via push + SMS (Twilio optional)",
      "Acknowledge / Accept / Resolve workflow with full audit trail",
      "Emergency analytics — median response time, first-time-fix rate",
    ],
    mockup: "escalation",
    liveUrl: `${LIVE}/settings/escalation`,
    stat: { value: "00:04", label: "median time to Tier-1 acknowledgement" },
    stats: [
      { value: "3", label: "escalation tiers" },
      { value: "00:04", label: "median acknowledge" },
      { value: "SMS+push", label: "to on-call, automatically" },
    ],
    pains: [
      { title: "3am phone-tag", desc: "One engineer doesn't pick up and the whole chain stalls until someone wakes the duty manager." },
      { title: "Emergencies buried in the job list", desc: "A burst pipe looks exactly like a dripping tap in a flat table of open jobs." },
      { title: "No proof of response", desc: "When the client asks 'how fast did you react?', the answer is a WhatsApp screenshot." },
      { title: "SLA penalties you can't see coming", desc: "Contract SLAs live in a PDF. Nothing in the tooling counts down." },
    ],
    deepDives: [
      { kicker: "Deep dive", title: "Escalation ladders that actually fire.", body: "If the first assignee doesn't acknowledge within your configured window, the job auto-escalates to the next tier — an on-call supplier, then the duty manager with SMS. Every hop is logged.", mockup: "escalation" },
      { kicker: "Red-alert channel", title: "Slack and Teams light up in red.", body: "Emergency events post to a dedicated #emergencies channel with Block Kit / MessageCard formatting: property, issue, tier, timer. Acknowledge from the message if you like.", mockup: "webhooks" },
    ],
    steps: [
      { title: "Flag", desc: "Raise as emergency from any portal — client, operative or office. It moves to the red pipeline instantly." },
      { title: "Notify", desc: "Tier 1 on-call receives push + SMS. The SLA timer starts counting in the UI and the Slack channel." },
      { title: "Escalate", desc: "No acknowledgement inside your window? Tier 2 fires automatically. Then Tier 3." },
      { title: "Resolve & report", desc: "Acknowledge / Accept / Resolve stamps flow into response analytics and the client's report." },
    ],
    faqs: [
      { q: "Can we set different ladders per client or property?", a: "Yes — ladders are configurable per tenant with overrides per contract or property." },
      { q: "Does SMS cost extra?", a: "SMS goes through your own Twilio account; push notifications and email are included." },
      { q: "What if the Tier-1 person is on holiday?", a: "On-call rotas and out-of-office delegation route Tier 1 to whoever is actually on shift." },
      { q: "Is emergency response its own add-on?", a: "Yes — Emergency Jobs is £15/month and included in every bundle from Starter upwards." },
    ],
    quote: { text: "The ladder ended 3am phone-tag. Tier 2 fires by itself and the client sees the timer.", name: "Sarah Okafor", role: "Operations Director, MetroCare FM" },
    related: ["jobs", "in-house", "integrations"],
  },
  {
    slug: "quotes",
    category: "core-ops",
    icon: FileText,
    name: "Quotes → Jobs → Projects",
    kicker: "Quote-to-cash",
    headline: "Quotes that turn into jobs. Or projects. Or both.",
    summary: "Win the work, click once, become a job or project. Quote templates, line items, markup ladders and client-approval links — all in one pipeline.",
    bullets: [
      "Quote templates library with VAT-aware pricing",
      "Convert any approved quote into a job (1-to-1) or project (multi-task)",
      "Send branded PDF quotes with a signed approval link",
      "Compare supplier quotes side-by-side in the comparison portal",
      "Markup labels are tenant-customisable (e.g. 'PM fee' vs 'Client uplift')",
    ],
    mockup: "quotecompare",
    liveUrl: LIVE,
    related: ["jobs", "invoicing", "supplier-portal", "ai-supplier-matching"],
  },
  {
    slug: "projects",
    category: "core-ops",
    icon: KanbanSquare,
    name: "Projects & Tasks",
    kicker: "Multi-task work",
    headline: "Refurbs, fit-outs and programmes — broken into tasks people can actually finish.",
    summary: "When a quote is bigger than one visit, promote it to a project. Tasks, owners, dependencies and budget burn on a board your whole team can see.",
    bullets: [
      "Kanban and list views with owners, due dates and dependencies",
      "Budget vs actual rolled up from task costings",
      "Tasks assignable to operatives or suppliers with the same picker",
      "Photos, files and job sheets attached per task",
      "Project-level invoicing — stage payments or on completion",
    ],
    mockup: "projects",
    liveUrl: LIVE,
    related: ["quotes", "jobs", "invoicing"],
  },
  {
    slug: "scheduling",
    category: "core-ops",
    icon: CalendarDays,
    name: "Scheduling & Calendar",
    kicker: "Time is the grid",
    headline: "See every job, every operative, every day.",
    summary: "Drag-and-drop calendar for admins. Live schedule for operatives. Automatic conflict detection, weekend cover and bank-holiday awareness.",
    bullets: [
      "Day / Week / Month calendar views",
      "Drag jobs onto operatives' rows to reassign",
      "Conflict detection — double-booked operatives flag instantly",
      "Routing-aware nudges when two jobs in one postcode could be a single visit",
      "Per-tenant timezone so briefs fire at 06:30 local, not UTC",
    ],
    mockup: "schedule",
    liveUrl: LIVE,
    related: ["in-house", "jobs", "contracts"],
  },
  {
    slug: "contracts",
    category: "core-ops",
    flagship: true,
    icon: Repeat,
    name: "Contracts & Recurring Visits",
    kicker: "Planned maintenance",
    headline: "Recurring work that schedules itself.",
    summary: "Cleaning rounds, fire-alarm tests, grounds maintenance, PPM visits — set the contract up once and Fixlr generates every visit, assigns your crew, tracks completion and rolls billing up per visit or per month.",
    bullets: [
      "Daily, weekly, fortnightly, monthly or ad-hoc schedules with weekday and time windows",
      "Visits auto-generated weeks ahead by a background scheduler — regenerated when the contract changes",
      "Multi-operative crews per visit, with the same assign picker you use for jobs",
      "Per-visit or monthly invoicing, client PO numbers and rate basis captured on the contract",
      "Clients can book extra visits themselves from the portal, inside your notice window",
      "Renewal and end-date tracking so nothing quietly lapses",
    ],
    mockup: "contracts",
    liveUrl: LIVE,
    stat: { value: "0", label: "missed rounds since go-live at MetroCare" },
    stats: [
      { value: "Weeks", label: "of visits generated ahead" },
      { value: "1", label: "monthly invoice per contract" },
      { value: "Self-serve", label: "client extra-visit booking" },
    ],
    pains: [
      { title: "The PPM spreadsheet", desc: "Someone owns a tab of dates. When they're off, the fire-alarm test doesn't happen." },
      { title: "Billing that lags delivery", desc: "Monthly contract invoices are built by hand from memory, weeks after the visits." },
      { title: "Clients ringing for extras", desc: "'Can you come Thursday as well?' becomes an email chain instead of a booking." },
      { title: "Silent lapses", desc: "Contracts end, renewals slip and the first you hear is a competitor's van outside." },
    ],
    deepDives: [
      { kicker: "Deep dive", title: "Completion, evidence and billing in the same thread.", body: "Operatives complete visits from their phone with photos and notes. Missed or late visits are flagged for the office; completed ones roll into the contract's invoicing run — per visit or one monthly invoice — with the client's PO number already on it.", mockup: "schedule" },
      { kicker: "How it works", title: "Let clients book, inside your rules.", body: "Turn on client booking per contract and set a minimum notice period. Clients raise an extra visit from their portal, it lands in your schedule as pending, and your team confirms with one tap.", mockup: "clientportal" },
    ],
    steps: [
      { title: "Define", desc: "Frequency, weekdays, time windows, crew size, rate basis and PO number on the contract." },
      { title: "Generate", desc: "The scheduler creates visits weeks ahead and regenerates if the contract changes." },
      { title: "Deliver", desc: "Crews see visits alongside one-off jobs in the same PWA and complete with evidence." },
      { title: "Bill", desc: "Completed visits roll into the contract's invoice run — per visit or one line per month." },
    ],
    faqs: [
      { q: "Can one contract cover multiple properties?", a: "Yes. Attach any number of properties; visits are generated per property with their own crews and windows." },
      { q: "What happens to visits if we change the frequency?", a: "Future visits are regenerated; completed and in-progress visits are preserved with their evidence." },
      { q: "Can visits be block-booked?", a: "Block bookings and ad-hoc schedules are supported alongside fixed recurrence." },
      { q: "Is this an add-on?", a: "Contracts is £20/month, included in Professional and Enterprise bundles." },
    ],
    quote: { text: "Twelve fire-alarm rounds a month used to live in a spreadsheet. Now they generate themselves and invoice themselves.", name: "Daniel Reeve", role: "Procurement Lead, Capital Facilities" },
    related: ["scheduling", "jobs", "client-portal", "invoicing"],
  },
  {
    slug: "clients-properties",
    category: "core-ops",
    icon: Building2,
    name: "Clients & Properties",
    kicker: "The map of what you look after",
    headline: "Every client, every site, every access note — one record.",
    summary: "Clients own properties; properties own assets, contracts, jobs and documents. Access notes, key-safe codes and contractor visibility rules live where your engineers will find them.",
    bullets: [
      "Client records with tiered portal access and billing details",
      "Properties with access notes, WhatsApp access and contractor visibility rules",
      "Full history of jobs, quotes, invoices and visits per property",
      "CSV import for clients and properties with guided mapping",
      "Property-limited client logins for site managers",
    ],
    mockup: "properties",
    liveUrl: LIVE,
    related: ["properties", "client-portal", "assets", "contracts"],
  },
  {
    slug: "properties",
    category: "core-ops",
    flagship: true,
    isNew: true,
    icon: Map,
    name: "Properties",
    kicker: "The portfolio, on one map",
    headline: "Every property you look after, on one map.",
    summary: "A dedicated Properties hub across all your clients and sub-clients: every registered address on a live map with its compliance status, statutory requirements and renewal clock, access notes and site contact, nearest engineers, full history, spend and a one-click audit pack.",
    bullets: [
      "Every registered property across clients and sub-clients — search by address, postcode or name, or jump in from the map",
      "Statutory requirements per property with renewal interval and lead time; renewal jobs raised automatically",
      "Access notes, key-safe codes, parking and site contact — shared only with the attending engineer",
      "Nearest engineers and suppliers to any property; spend by property and 3-year replacement forecast",
      "Move, merge and bulk-import properties; branded tenant reporting forms pre-bound to each address",
    ],
    mockup: "portfolio",
    liveUrl: `${LIVE}/properties`,
    stat: { value: "1 map", label: "for the whole portfolio" },
    stats: [
      { value: "1 map", label: "for the whole portfolio" },
      { value: "auto", label: "renewal jobs at lead time" },
      { value: "1 click", label: "audit pack per property" },
    ],
    pains: [
      { title: "Addresses typed four different ways", desc: "The same block exists three times — once per spreadsheet — and nobody knows which one is right." },
      { title: "Which certificate covers which building?", desc: "Statutory compliance tracked per supplier, not per property, so an EICR lapses quietly on a site nobody checked." },
      { title: "Access info in someone's head", desc: "Key-safe codes and parking live in a WhatsApp thread; the new engineer rings the office from the kerb." },
      { title: "No view of where the money goes", desc: "Spend per property, and what is likely to need replacing next year, takes a week of pivot tables." },
    ],
    deepDives: [
      { kicker: "Deep dive", title: "One record per property. Everything hangs off it.", body: "Jobs, assets, statutory requirements, access notes, documents, contracts and spend all live on the property record — drill down from the main client to its sub-clients, or search straight to an address. Changing an access note updates every future job at that property.", mockup: "propertyhub" },
      { kicker: "How it works", title: "Statutory duties with a clock on them.", body: "Each property carries its statutory requirements — Gas Safety, EICR, fire risk assessment, LOLER, legionella, PAT — with renewal interval and lead time. At lead time Fixlr raises the renewal job; the completed job sheet files the certificate back to the property and rolls the date. Export the audit pack whenever a client asks.", mockup: "compliancecalendar" },
    ],
    steps: [
      { title: "Import", desc: "CSV import with guided mapping, or create properties under each client and sub-client. Duplicates are detected and merged." },
      { title: "Set duties", desc: "Pick the statutory requirements that apply; renewal interval and lead time are set per requirement type." },
      { title: "Work", desc: "Raise jobs, contracts and tenant forms against the property; access notes go to whoever attends." },
      { title: "Prove", desc: "History, certificates, spend and forecast per property — plus a downloadable audit pack." },
    ],
    faqs: [
      { q: "How is this different from Clients & Properties?", a: "Clients & Properties is the client record and its portfolio list. The Properties hub is the cross-client view: the map, statutory requirements, audit packs, nearest engineers, spend and forecast for every property in one place." },
      { q: "Can clients see their own properties?", a: "Yes — in Client Portal they see their portfolio, each property's certificates and what's due, and can raise requests pre-bound to the address." },
      { q: "Can we move a property between clients?", a: "Yes — drag a property to another account, or tick several and move them at once. History travels with it; duplicates on the target account are skipped and reported." },
      { q: "Is Properties an add-on?", a: "No — it's part of the core platform, included in every seat." },
    ],
    quote: { text: "I can finally answer 'what's due at Kingsway this quarter?' from one screen — and export the audit pack while the client is still on the phone.", name: "Priya Nair", role: "Managing Director, Arc Properties" },
    related: ["clients-properties", "compliance-hub", "assets", "asset-tagging"],
  },

  {
    slug: "email-inbox",
    category: "core-ops",
    flagship: true,
    isNew: true,
    icon: Inbox,
    name: "Email-to-Job Inbox",
    kicker: "Intake on autopilot",
    headline: "Every email becomes a job. Nobody re-types anything.",
    summary: "Connect or forward your maintenance mailbox and Fixlr reads every email — tenant complaints, client requests, supplier updates — matches the sender to a client and property, extracts the issue, urgency and attachments, and turns it into a triaged job in one click.",
    bullets: [
      "Connect Google Workspace / Microsoft 365, or forward to your tenant's in.fixlr.app address",
      "AI extracts property, issue, trade, urgency and asset from the body and attachments",
      "Sender matched to a client, property and contact — new ones created in a click",
      "Replies thread back onto the job; the requester gets status updates automatically",
      "Supplier emails (quotes, completion certs, invoices) file straight onto the right job",
    ],
    mockup: "emailinbox",
    demo: "email",
    liveUrl: `${LIVE}/inbox`,
    stat: { value: "12s", label: "from email to triaged job" },
    stats: [
      { value: "12s", label: "email → job card" },
      { value: "0", label: "re-typed requests" },
      { value: "100%", label: "threads kept on the job" },
    ],
    pains: [
      { title: "Your inbox is the job board", desc: "Hundreds of requests a week live in Outlook folders — flagged, starred and forgotten." },
      { title: "Copy, paste, repeat", desc: "An admin re-types the address, the problem and the phone number — and a typo sends the engineer to the wrong block." },
      { title: "Which property? Which client?", desc: "'The tap in flat 4' with no building name. Three emails later, you know." },
      { title: "Reply-all chaos", desc: "Updates go out by email, come back by email, and the job record never hears about it." },
    ],
    deepDives: [
      { kicker: "Deep dive", title: "Read, matched, triaged — before you open it.", body: "Fixlr reads the sender, subject, body and attachments. It matches the sender to a client contact and property (or suggests one from the address), classifies the trade and urgency, spots asset references and drafts the job title and description. You review a card, not an email.", mockup: "emailtriage" },
      { kicker: "How it works", title: "The thread lives on the job, forever.", body: "Convert with one click and the whole email thread — including every later reply — is attached to the job. Status changes and ETAs go back to the requester from the same thread, so clients never have to learn a new tool to hear from you.", mockup: "jobs" },
    ],
    steps: [
      { title: "Connect", desc: "OAuth to Google Workspace or Microsoft 365, or set a forwarding rule to your tenant's in.fixlr.app address. Two minutes." },
      { title: "Read", desc: "Every inbound email is parsed: sender, property, issue, trade, urgency, asset and attachments." },
      { title: "Convert", desc: "One click turns the card into a job, assigned via the usual picker or AI Supplier Matching. Emergencies route to the emergency pipeline." },
      { title: "Close the loop", desc: "Replies, ETAs and completion photos go back to the requester on the original thread." },
    ],
    faqs: [
      { q: "Which mailboxes are supported?", a: "Google Workspace and Microsoft 365 via OAuth, plus any mailbox that can forward to your tenant's unique in.fixlr.app address." },
      { q: "What if the sender isn't a known contact?", a: "Fixlr suggests a client and property from the address and signature; you confirm or create them in one click, and the mapping is remembered." },
      { q: "Does it convert everything automatically?", a: "By default you approve each card. You can enable auto-convert per sender or domain — most teams do this for their biggest clients." },
      { q: "Is Email-to-Job Inbox an add-on?", a: "Yes — a paid add-on. Pricing is being finalised; it's included in every trial today and sales can quote it for your plan." },
    ],
    quote: { text: "Monday mornings used to be 90 minutes of re-typing tenant emails. Now it's ten minutes of clicking approve.", name: "Priya S.", role: "Operations Coordinator, Arc Properties" },
    related: ["jobs", "whatsapp", "client-portal", "emergency"],
  },
  // ---------------- PORTALS ----------------
  {
    slug: "in-house",
    category: "portals",
    icon: Smartphone,
    name: "In-House Team Portal",
    kicker: "Your team, on the ground",
    headline: "A PWA that turns your van into a command centre.",
    summary: "Install Fixlr on any phone in 10 seconds. Push notifications for new jobs. Today's Snapshot at the top. Job sheets to tap through. Directions wired to Google Maps. Mark as Arrived logs the time.",
    bullets: [
      "Install as a PWA on iOS + Android — no app store needed",
      "Real-time push notifications for new job assignments",
      "Today's Snapshot widget: Up Next / Done Today / Need Time Logged",
      "Morning briefing email at 06:30 local + EoD wrap at 18:00",
      "Tap-through job sheets with signature capture and photo upload",
      "Mark as Arrived auto-stamps time on site for billing accuracy",
    ],
    mockup: "pwa",
    liveUrl: `${LIVE}/my-work`,
    related: ["navigation", "jobs", "client-portal"],
  },
  {
    slug: "navigation",
    category: "portals",
    icon: MapPin,
    name: "Navigation & Geo-tagging",
    kicker: "Where is the team?",
    headline: "Live team map with geo-tagged arrivals.",
    summary: "See every operative on a real map. Watch them arrive on site. Share a live ETA tracker with the client. Built into the same PWA — no extra apps.",
    bullets: [
      "Live Team Map — real-time positions of on-shift operatives",
      "Mark as Arrived stamps GPS + time when the operative hits the doorstep",
      "Public Share Tracker — client gets a live ETA link they can watch",
      "Get Directions deep-links to Google Maps / Apple Maps",
      "Auto-calculated on-site minutes roll straight into billing",
    ],
    mockup: "livemap",
    liveUrl: LIVE,
    related: ["in-house", "client-portal", "route-planner"],
  },
  {
    slug: "client-portal",
    category: "portals",
    flagship: true,
    icon: UserCircle2,
    name: "Client Portal",
    kicker: "Self-service, branded",
    headline: "Clients raise requests. Approve quotes. Pay invoices. Without calling you.",
    summary: "Give every client a secure, white-labelled portal with real logins. They see their properties, live jobs, contracts, quotes and invoices; raise new requests 24/7 with photos; approve quotes online; and pay in one tap. Your team stops being the switchboard.",
    bullets: [
      "Real client accounts with tiered access (full, view-only, property-limited) — invited from the client record",
      "Dashboard of live jobs, upcoming contract visits, open quotes and unpaid invoices",
      "Raise a request in 60 seconds with photos, urgency and property — it lands straight in your Requests queue",
      "Approve or decline quotes online with digital sign-off; you're alerted instantly",
      "Download invoices, see payment status and pay via Stripe from the email or the portal",
      "Property access notes, WhatsApp access and contractor visibility rules per client",
      "Your logo, your colours, your domain-style login page — clients never see 'Fixlr'",
    ],
    mockup: "clientportal",
    liveUrl: LIVE,
    stat: { value: "60s", label: "to raise a request with photos" },
    stats: [
      { value: "60s", label: "to raise a request" },
      { value: "24/7", label: "quote approvals" },
      { value: "1-tap", label: "Stripe payment" },
    ],
    pains: [
      { title: "You are the switchboard", desc: "Every 'any update on the leak?' lands on your phone, your inbox and your evening." },
      { title: "Quotes stuck in email", desc: "PDF sent Tuesday, approved verbally Friday, disputed in March." },
      { title: "Chasing payment", desc: "Invoices are attachments. Paying them means a bank transfer and a reference typed wrong." },
      { title: "Sensitive access info in WhatsApp", desc: "Key-safe codes and alarm details scattered across chats with no visibility rules." },
    ],
    deepDives: [
      { kicker: "Deep dive", title: "Requests in, phone calls out.", body: "A guided request form captures the property, what's wrong, urgency and photos. Requests arrive in your queue already categorised and matched to the right trade and area — with the client's contract and history attached.", mockup: "clientrequests" },
      { kicker: "How it works", title: "Quotes approved, invoices paid — from anywhere.", body: "Clients approve quotes with one click and a digital signature, and see the resulting job appear. Invoices show live payment status, reminders go out automatically and Stripe pay links mean money arrives without a chase.", mockup: "clientinvoices" },
    ],
    steps: [
      { title: "Invite", desc: "From the client record: full, view-only or property-limited access. They get a branded login page." },
      { title: "Request", desc: "Client picks the property, describes the issue, adds photos and urgency. It's in your queue in seconds." },
      { title: "Approve", desc: "Quote arrives in the portal and by email; one click and an e-signature approves it." },
      { title: "Pay", desc: "Invoice lands with a Stripe pay-link. Status updates live for both sides." },
    ],
    faqs: [
      { q: "Do client users count towards our seats?", a: "No. Clients and suppliers on their portals never count as users." },
      { q: "Can a client see contractor names?", a: "That's up to you — contractor visibility is a per-client rule." },
      { q: "Can clients book contract visits?", a: "Yes, when you enable client booking on a contract with a minimum notice period." },
      { q: "Can we use our own domain for the portal?", a: "Yes, with the Custom Domain add-on (auto-TLS) — or fully remove Fixlr branding with White-label." },
    ],
    quote: { text: "Clients approve quotes at 9pm from the portal and we've invoiced by the time the van leaves.", name: "Priya Nair", role: "Managing Director, Arc Properties" },
    related: ["contracts", "quotes", "invoicing", "white-label"],
  },
  {
    slug: "route-planner",
    category: "portals",
    icon: Route,
    name: "Route Planner & Mileage",
    kicker: "Fewer miles, more jobs",
    headline: "Optimised daily routes and a mileage log that writes itself.",
    summary: "Give every operative an ordered day from their home location, nudge the office when two jobs share a postcode, and log mileage for expenses automatically.",
    bullets: [
      "Optimised stop order from each operative's home location",
      "Mileage log per day, per job — export for expenses and HMRC",
      "Postcode-clustering suggestions when planning the week",
      "One-tap directions to the next stop from the PWA",
      "Travel time vs on-site time reporting",
    ],
    mockup: "route",
    liveUrl: LIVE,
    related: ["navigation", "scheduling", "in-house"],
  },
  {
    slug: "whatsapp",
    category: "portals",
    icon: MessageCircle,
    name: "WhatsApp Inbox",
    kicker: "Meet suppliers where they are",
    headline: "Two-way WhatsApp, inside the job.",
    summary: "Conversations with suppliers and clients land in a shared inbox linked to the job — with one-tap job offers suppliers can accept from their phone.",
    bullets: [
      "Shared WhatsApp inbox linked to jobs, quotes and requests",
      "One-tap job offers — supplier replies 'accept' and it's assigned",
      "Templates for ETA, arrival and completion updates",
      "Media from WhatsApp saved to the job's files automatically",
      "Per-property WhatsApp access rules for residents and site staff",
    ],
    mockup: "whatsapp",
    liveUrl: LIVE,
    related: ["supplier-portal", "jobs", "client-portal"],
  },

  // ---------------- SUPPLIER LANE ----------------
  {
    slug: "supplier-portal",
    category: "supplier-lane",
    flagship: true,
    icon: Store,
    name: "Supplier Portal",
    kicker: "External vendors, managed",
    headline: "A dedicated portal for every supplier you work with.",
    summary: "They log in, see their open jobs, submit quotes, upload completion docs, get paid. You approve, assign and compare — from a single dashboard.",
    bullets: [
      "Per-supplier login with a tenant-isolated view of their jobs only",
      "Submit quotes directly — no more email PDF back-and-forth",
      "Upload completion reports, photos, RAMS",
      "Track payments and download self-service statements",
      "En-route / Arrived / Complete status buttons (mobile-friendly)",
    ],
    mockup: "supplierportal",
    liveUrl: "https://supplier.fixlr.app/northheat/dashboard",
    stat: { value: "96%", label: "SLA hit on supplier-delivered jobs" },
    stats: [
      { value: "0", label: "PDF quotes by email" },
      { value: "96%", label: "SLA hit rate" },
      { value: "Free", label: "for every supplier" },
    ],
    pains: [
      { title: "Quotes as PDF attachments", desc: "Three suppliers, three formats, one spreadsheet to compare them and a week gone." },
      { title: "No status until they invoice", desc: "You find out the job was done when the bill arrives — or when the client rings." },
      { title: "Completion docs in your inbox", desc: "Photos and RAMS arrive as WhatsApp images that never make the job file." },
      { title: "'Have you paid us yet?'", desc: "Suppliers chase statements; your finance team chases suppliers for invoices." },
    ],
    deepDives: [
      { kicker: "Deep dive", title: "En-route, arrived, complete — from the van.", body: "The same mobile UX as your in-house team. Suppliers feel like a natural extension of your operation, and you get the same GPS-stamped arrival and photo evidence.", mockup: "suppliermobile" },
      { kicker: "Quotes", title: "Structured quotes you can compare.", body: "Suppliers quote against your request with line items and VAT. Multiple quotes on the same request appear side-by-side with rating, availability and compliance — pick a winner in one click.", mockup: "quotecompare" },
    ],
    steps: [
      { title: "Invite", desc: "From the supplier record or a CSV bulk invite. They accept, upload certs and get a login." },
      { title: "Offer", desc: "Assign a job or request a quote. They see it in their portal and on their phone." },
      { title: "Deliver", desc: "En-route → Arrived → Complete with photos, RAMS and completion notes." },
      { title: "Get paid", desc: "Invoice against the job; you approve; they see status and download statements." },
    ],
    faqs: [
      { q: "Do suppliers pay anything?", a: "No. The portal is free for suppliers and they never count towards your seats." },
      { q: "Can a supplier see other suppliers' jobs?", a: "Never. Each supplier's view is tenant- and supplier-isolated." },
      { q: "Can suppliers upload compliance documents themselves?", a: "Yes — and one-click re-upload links let them fix gaps without logging in." },
      { q: "Is the portal white-labelled?", a: "Yes, it carries your brand, and with Custom Domain it lives on your domain." },
    ],
    quote: { text: "Suppliers actually use it. Photo sign-off from their portal ended the 'was it done?' calls.", name: "Daniel Reeve", role: "Procurement Lead, Capital Facilities" },
    related: ["supplier-network", "supplier-compliance", "marketplace"],
  },
  {
    slug: "marketplace",
    category: "supplier-lane",
    flagship: true,
    isNew: true,
    icon: Store,
    name: "Contractor Marketplace",
    kicker: "Vetted contractors, on tap",
    headline: "Need a trade you don't have? Vetted contractors, near the job.",
    summary: "Browse Fixlr-vetted contractors by trade and area — insurance, accreditations and reviews checked — then request quotes, offer jobs and message them without leaving Fixlr. Compare up to three side by side; accept, and they become a supplier on the job with a portal login.",
    bullets: [
      "Search by trade and radius; filter on accreditations (Gas Safe, NICEIC, CHAS, SafeContractor), insurance and rating",
      "Compare up to three contractors; favourite the ones you like; saved searches alert you to new listings",
      "Request quotes with photos and the RAMS template attached; offer a job directly; message in-thread",
      "Accept a quote and the contractor is created as a supplier, assigned to the job and sent a Supplier Portal login",
      "Client name hidden until accepted; reported listings are hidden automatically while Fixlr reviews",
    ],
    mockup: "marketplace",
    liveUrl: `${LIVE}/marketplace`,
    crossLink: { label: "Are you a contractor? List your business — free", to: "/for-contractors" },
    stat: { value: "£25", label: "per month, unlimited requests" },
    stats: [
      { value: "3", label: "contractors compared at once" },
      { value: "vetted", label: "insurance · accreditations · reviews" },
      { value: "0", label: "commission on the work" },
    ],
    pains: [
      { title: "Google and hope", desc: "A new trade in a new town means an hour of calls to find anyone who'll pick up — let alone send insurance." },
      { title: "Vetting from scratch, every time", desc: "Chasing public liability certificates and accreditation numbers before you can even ask for a price." },
      { title: "Quotes by email, decisions by memory", desc: "Three PDFs in three threads; nobody can see them side by side." },
      { title: "The good ones you can't find again", desc: "The roofer who was brilliant in March is a number in someone's phone." },
    ],
    deepDives: [
      { kicker: "Deep dive", title: "Quote requests that come back answered.", body: "Send the job — scope, photos, access, asset manual and your RAMS template — to up to three contractors in one go. Replies land side by side with price, date and terms. Accept one and the supplier record, job assignment and portal login happen automatically; the client's name is revealed only then.", mockup: "quoterequest" },
      { kicker: "For contractors", title: "A listing every Fixlr customer can see.", body: "Contractors list once — same login, their own listing, verified documents and reviews from real jobs. Every Monday at 09:00 they get a digest of their inbox plus anonymised demand near them. Listings edited after approval stay live on the previous version until Fixlr approves the change.", mockup: "listing" },
    ],
    steps: [
      { title: "Search", desc: "Trade, radius from the job or property, accreditations, rating. Favourites and saved searches remembered." },
      { title: "Compare", desc: "Up to three side by side: documents, coverage, reviews, response time." },
      { title: "Request or offer", desc: "Ask for quotes with the job pack attached, or offer the job straight out. Message in-thread." },
      { title: "Accept", desc: "Supplier created, job assigned, Supplier Portal login sent; compliance docs already on file." },
    ],
    faqs: [
      { q: "How are contractors vetted?", a: "Fixlr checks insurance, accreditations and identity before a listing goes live, tracks document expiry, and shows reviews from completed Fixlr jobs. Listings reported by three customers are hidden automatically while we review." },
      { q: "Does Fixlr take a cut of the work?", a: "No. The Marketplace add-on is a flat £25/month per workspace with unlimited requests; the price you agree is between you and the contractor." },
      { q: "Can my existing suppliers be found by other customers?", a: "Only if they choose to list. Your private supplier network stays private; the Marketplace is opt-in for contractors who want more work." },
      { q: "Is the Marketplace an add-on?", a: "Yes — £25/month, available on any plan and included in Enterprise." },
    ],
    quote: { text: "A lift engineer in Leeds at 4pm on a Friday, insured and on site Monday — from a search, not a phone marathon.", name: "Marcus Oyelaran", role: "Head of Facilities, Capital Holdings" },
    related: ["supplier-network", "supplier-portal", "supplier-compliance", "ai-supplier-matching"],
  },
  {
    slug: "supplier-network",
    category: "supplier-lane",
    icon: Network,
    name: "Supplier Network",
    kicker: "Onboarding & marketplace",
    headline: "Bulk-invite 100 suppliers in 10 minutes.",
    summary: "CSV upload, custom application forms, automated compliance checks and a marketplace feed of available contractors. Scale your supplier base without an admin team.",
    bullets: [
      "Bulk supplier invite (CSV + email templates)",
      "Public supplier-application page — they apply, you approve",
      "Trade / location / rating filters on the supplier directory",
      "Accept-invite onboarding flow with document upload",
      "Approval workflow with audit trail",
    ],
    mockup: "bulkinvite",
    liveUrl: `${LIVE}/platform/bulk-supplier-invite`,
    related: ["supplier-portal", "supplier-compliance", "ai-supplier-matching"],
  },
  {
    slug: "supplier-compliance",
    category: "supplier-lane",
    icon: ShieldCheck,
    name: "Supplier Compliance",
    kicker: "Sleep well at night",
    headline: "Every cert. Every expiry. Every time.",
    summary: "Track insurance, RAMS, DBS and trade-specific certs per supplier. Auto-email before expiry. Block assignments when a cert lapses. Pass audits, every time.",
    bullets: [
      "Per-supplier compliance document library with expiry dates",
      "Scheduler auto-emails 30/14/7/1 days before expiry",
      "Hard block: can't assign a job to a non-compliant supplier",
      "One-screen compliance dashboard across your entire supplier base",
      "Downloadable audit packs per supplier for insurance / procurement reviews",
    ],
    mockup: "compliancematrix",
    liveUrl: LIVE,
    related: ["supplier-portal", "supplier-network", "compliance-hub"],
  },

  // ---------------- MANAGEMENT ----------------
  {
    slug: "invoicing",
    category: "management",
    flagship: true,
    icon: Receipt,
    name: "Invoicing & Payments",
    kicker: "Money in",
    headline: "Line-item invoices. Live totals. Stripe one-tap pay.",
    summary: "Generate branded invoices from completed jobs or build them manually. Line-item editor with live totals. Markup A (your margin) + Markup B (client uplift). Stripe integration for one-tap client payment.",
    bullets: [
      "Manual or auto-generate from job / quote",
      "Editable line items with server-recomputed totals (anti-tamper)",
      "Two-layer markup system with custom labels",
      "Draft → Sent → Paid lifecycle with lock-on-sent",
      "Stripe pay-links on every invoice",
      "Branded PDF export",
    ],
    mockup: "invoice",
    liveUrl: LIVE,
    stat: { value: "Same hour", label: "job completes → invoice sent" },
    stats: [
      { value: "2", label: "markup layers" },
      { value: "1-tap", label: "Stripe payment" },
      { value: "Locked", label: "totals once sent" },
    ],
    pains: [
      { title: "Invoices built from memory", desc: "Parts and hours re-typed from job notes weeks later. Margin leaks with every line." },
      { title: "Bank transfers and wrong references", desc: "Getting paid means the client finding the PDF, logging into their bank and typing INV-0042 correctly." },
      { title: "Who changed the total?", desc: "A spreadsheet total edited after sending, and a dispute nobody can settle." },
      { title: "Margin is a secret", desc: "Your uplift and the client's surcharge are mixed into one number no one can explain." },
    ],
    deepDives: [
      { kicker: "Deep dive", title: "Markup A. Markup B. Both explainable.", body: "Your margin and the client's uplift are separate, labelled layers — 'PM fee', 'Client uplift', whatever your contracts call them. Totals are recomputed on the server so a tampered line never reaches a PDF.", mockup: "costlines" },
      { kicker: "How it works", title: "Draft → Sent → Paid, with Stripe doing the chasing.", body: "Once sent, the invoice locks. The client gets a branded email with a pay-link; reminders go automatically; payment status updates on both sides the second Stripe confirms.", mockup: "clientinvoices" },
    ],
    steps: [
      { title: "Generate", desc: "From a completed job or approved quote — or build one manually." },
      { title: "Refine", desc: "Edit lines, apply Markup A/B, VAT per line. Totals recompute live." },
      { title: "Send", desc: "Branded PDF + email with Stripe pay-link. Invoice locks on send." },
      { title: "Reconcile", desc: "Paid status flows back automatically; statements available to clients and suppliers." },
    ],
    faqs: [
      { q: "Do we need a Stripe account?", a: "Yes — connect your own Stripe account and payments settle directly to you." },
      { q: "Can we invoice contracts monthly?", a: "Yes. Contract visits roll into a single monthly invoice with the client's PO number." },
      { q: "Can we export to our accounts package?", a: "CSV export and webhooks on invoice events are included; API access is available as an add-on." },
      { q: "Is VAT handled?", a: "Per-tenant VAT settings apply to the invoices you send; Stripe handles VAT on your Fixlr subscription." },
    ],
    quote: { text: "We invoice the same hour the job completes, with a pay-link. Cash flow is a different animal.", name: "Priya Nair", role: "Managing Director, Arc Properties" },
    related: ["jobs", "quotes", "accounting", "client-portal"],
  },
  {
    slug: "accounting",
    category: "management",
    flagship: true,
    isNew: true,
    icon: Landmark,
    name: "Accounts & Bookkeeping",
    kicker: "The books, inside the ops",
    headline: "The books, reconciled against the jobs that caused them.",
    summary: "Open Banking feeds match themselves to invoices, bills and receipts using rules you set; operatives snap receipts on the phone; VAT returns file to HMRC under Making Tax Digital; payroll runs with payslips, FPS, P60s and leave. Already on Xero, QuickBooks, FreeAgent or Sage? Keep them in sync.",
    bullets: [
      "Open Banking bank feeds (TrueLayer) with statement import fallback; auto-matching with rules and one-click 'match & post'",
      "Supplier bills, receipts and expense claims — snap on the phone, match to a job, approve, mark paid or reimburse",
      "Making Tax Digital VAT: boxes 1–9 built from your books, obligations pulled from HMRC, submit and keep the receipt",
      "Payroll: employees, runs, payslip PDFs, FPS to HMRC, P60/P45, leave & absence with cover requests and calendar links",
      "Two-way sync with Xero, QuickBooks Online, FreeAgent, Sage Accounting and Zoho Books; GoCardless Direct Debit; monthly client statements",
    ],
    mockup: "accounts",
    liveUrl: `${LIVE}/accounts`,
    stat: { value: "MTD", label: "VAT filed straight to HMRC" },
    stats: [
      { value: "auto", label: "bank lines matched by rules" },
      { value: "MTD", label: "VAT & payroll FPS to HMRC" },
      { value: "5", label: "accounting packages kept in sync" },
    ],
    pains: [
      { title: "Two systems that don't agree", desc: "Jobs in one place, money in another; month-end is a reconciliation hunt between them." },
      { title: "Receipts in a glovebox", desc: "Materials bought on site arrive as a shoebox at quarter-end — unmatched, unbilled, VAT unclaimed." },
      { title: "VAT return by spreadsheet", desc: "Exporting, re-keying, hoping the numbers tie up before the deadline." },
      { title: "Payroll somewhere else again", desc: "Timesheets in Fixlr, hours re-typed into a payroll tool, payslips emailed by hand." },
    ],
    deepDives: [
      { kicker: "Deep dive", title: "Every bank line finds its paperwork.", body: "Each feed line is matched to the invoice, bill, receipt or expense claim it belongs to — with confidence, the job it relates to and whether it's billable. Rules learn from what you accept: 'Screwfix → Materials, ask for the job, match receipts within £1'. The rest is a review queue, not a spreadsheet.", mockup: "bankrec" },
      { kicker: "How it works", title: "VAT and payroll filed from the same books.", body: "The VAT return is built from reconciled sales and purchases for the HMRC period, with obligations and submission receipts pulled from HMRC. Payroll runs use timesheets and labour rates already in Fixlr; approve the run to generate payslips, file the FPS and send P60s and P45s when needed.", mockup: "vatreturn" },
    ],
    steps: [
      { title: "Connect", desc: "Link bank accounts via Open Banking (or import statements), and optionally Xero, QuickBooks, FreeAgent, Sage or Zoho Books." },
      { title: "Capture", desc: "Invoices and bills flow from jobs; operatives snap receipts; purchase orders let them buy locally and be reimbursed." },
      { title: "Match", desc: "Rules auto-match the obvious; you clear the review queue with 'match & post'." },
      { title: "File", desc: "Submit VAT under MTD, approve payroll with FPS, send client statements — all from the same books." },
    ],
    faqs: [
      { q: "Do I still need Xero or QuickBooks?", a: "Not necessarily — Fixlr keeps full books, VAT and payroll on its own. If your accountant lives in Xero, QuickBooks, FreeAgent, Sage or Zoho, connect it and Fixlr pushes invoices and pulls payments, with your account mapping." },
      { q: "Is the VAT submission HMRC-recognised?", a: "Yes — Fixlr files under Making Tax Digital through HMRC's API, pulls your obligations and stores the submission receipt against each period. Flat-rate and standard schemes are supported." },
      { q: "Which banks are supported?", a: "Any UK bank available through Open Banking (TrueLayer). Where a feed isn't available, statement import keeps you reconciled." },
      { q: "Is Accounts & Bookkeeping an add-on?", a: "No — it's a core module, included in every seat. Advanced Analytics adds P&L by client and supplier scorecards on top." },
    ],
    quote: { text: "Month-end used to be three days of matching. Now the feed is reconciled by Tuesday and the VAT return is a button.", name: "Daniel Reeve", role: "Procurement Lead, Capital Facilities" },
    related: ["invoicing", "in-house", "advanced-analytics", "integrations"],
  },
  {
    slug: "assets",
    category: "management",
    icon: Boxes,
    name: "Asset Register",
    kicker: "Every boiler, every pump, every door",
    headline: "Know what you own. Know when it breaks.",
    summary: "Register every asset (boilers, lifts, HVAC, fire doors) against its property. Every job posts against an asset. See maintenance history, warranty expiry and replacement-cost projections.",
    bullets: [
      "Unlimited assets per property with QR-code stickers",
      "Scan a QR → raise a job against that asset in one tap",
      "Full maintenance history per asset",
      "Predictive maintenance alerts based on service-interval rules",
      "Warranty + lifecycle tracking",
    ],
    mockup: "asset",
    liveUrl: LIVE,
    related: ["jobs", "job-sheets", "contracts"],
  },
  {
    slug: "asset-tagging",
    category: "management",
    flagship: true,
    isNew: true,
    icon: ScanLine,
    name: "Asset Tagging",
    kicker: "QR + NFC on every asset",
    headline: "Tap or scan. The asset tells you everything.",
    summary: "Print QR labels or write NFC tags for every boiler, lift, panel and fire door. Any phone that taps or scans lands on that asset's page — history, documents, open jobs and a one-tap 'raise a job' — and every scan is logged as proof of presence.",
    bullets: [
      "QR label sheets and NFC tags generated from the asset register — Avery-ready PDFs",
      "No app needed: a scan opens the asset page in any phone browser",
      "Raise a job, log a reading or complete a check against the exact asset in one tap",
      "Every scan stamped with who, where and when — proof of attendance for PPM and audits",
      "Operatives and suppliers see the asset's RAMS, certificates and manuals on site",
    ],
    mockup: "tagscan",
    liveUrl: `${LIVE}/assets/tags`,
    stat: { value: "1 tap", label: "to raise a job on site" },
    stats: [
      { value: "1 tap", label: "to raise a job on site" },
      { value: "GPS+time", label: "stamped on every scan" },
      { value: "0", label: "apps to install" },
    ],
    pains: [
      { title: "Which boiler is this?", desc: "Three identical units in the plant room and a job sheet that says 'boiler'." },
      { title: "History lives in a filing cabinet", desc: "The engineer on site can't see the last service, the fault log or the manual." },
      { title: "Proof of attendance is a photo of a door", desc: "Clients ask whether the PPM visit happened. You hope the photos are dated." },
      { title: "Barcode kit from 2009", desc: "Dedicated scanners, a server in a cupboard, and labels nobody reprints." },
    ],
    deepDives: [
      { kicker: "Deep dive", title: "Labels and tags straight from the register.", body: "Select assets, choose QR, NFC or both, and download a print-ready sheet (or write tags from your phone). Each tag carries a signed, tenant-scoped URL — no app, no login for a read-only view, full actions for your team and suppliers.", mockup: "tagsheet" },
      { kicker: "How it works", title: "Every scan becomes a record.", body: "A scan logs who, when and where (GPS) against the asset. Chain scans into a PPM round and Fixlr proves the visit happened, in order, on time — and raises a job automatically if a check fails.", mockup: "asset" },
    ],
    steps: [
      { title: "Register", desc: "Assets live against properties with make, model, install date, warranty and service interval." },
      { title: "Tag", desc: "Print QR sheets or write NFC tags; stick them on the asset, the panel or the door frame." },
      { title: "Scan", desc: "Any phone opens the asset page: history, docs, open jobs, readings, RAMS." },
      { title: "Act", desc: "Raise a job, complete a check, log a meter reading — the scan is the audit trail." },
    ],
    faqs: [
      { q: "QR or NFC — which should we use?", a: "Both are generated from the same asset. QR is cheapest and works through glass; NFC survives paint, grime and outdoor use. Many teams use both on critical plant." },
      { q: "Do suppliers need an account to scan?", a: "No. A scan shows the public asset summary; suppliers with Supplier Portal access can act on it, and your team gets full actions." },
      { q: "Can residents or tenants scan?", a: "Yes — a tenant scanning a tag in a communal area gets a 'report a problem' form that lands on the right asset and property." },
      { q: "Is Asset Tagging an add-on?", a: "Yes — a paid add-on alongside the core Asset Register. Pricing is being finalised; it's included in every trial today." },
    ],
    quote: { text: "Engineers tap the tag, see the last three services and the manual, and raise follow-ups on the spot. The clipboard is gone.", name: "Marcus O.", role: "Estates Manager, North Tower Group" },
    related: ["assets", "jobs", "compliance-hub", "contracts"],
  },
  {
    slug: "job-sheets",
    category: "management",
    icon: FileSignature,
    name: "Job Sheets & Forms",
    kicker: "Digital paperwork",
    headline: "Every clipboard, gone. Every signature, captured.",
    summary: "Design custom job-sheet templates (gas safety, electrical test, fire inspection). Attach automatically to every job. Operatives fill them in the field. PDFs get stitched into the completion pack.",
    bullets: [
      "Template designer — drag-and-drop form builder",
      "Auto-attach by job category (e.g. 'any gas job gets the gas safety sheet')",
      "Mobile-first filling — signature capture, photo upload, checkboxes",
      "Completion PDF auto-generated and attached to the job",
      "Shared across all operatives in real time",
    ],
    mockup: "jobsheet",
    liveUrl: LIVE,
    related: ["jobs", "assets", "in-house"],
  },
  {
    slug: "reports",
    category: "management",
    icon: BarChart3,
    name: "Reports & Dashboards",
    kicker: "See everything",
    headline: "The stats that matter, refreshed every minute.",
    summary: "Revenue, job volume, SLA hit-rate, first-time fix, vendor performance, operative utilisation. Export any chart to PDF or CSV. Scheduled weekly digest email.",
    bullets: [
      "Live SaaS dashboard for platform admins (revenue, tenant growth, churn)",
      "Per-tenant operations dashboard (jobs, SLAs, supplier stats)",
      "Emergency response analytics",
      "Scheduled weekly digest email",
      "Export to PDF or CSV from every chart",
    ],
    mockup: "reports",
    liveUrl: LIVE,
    related: ["jobs", "emergency", "advanced-analytics"],
  },
  {
    slug: "compliance-hub",
    category: "management",
    flagship: true,
    icon: ShieldAlert,
    name: "Compliance Hub",
    kicker: "Single pane of glass",
    headline: "Every certificate. One hub. Zero surprises.",
    summary: "Insurance, Gas Safe, NICEIC, RAMS, toolbox talks and every statutory obligation per property — tracked in one hub with a live expiry calendar, instant alerts the moment anything lapses, one-click audit packs and re-upload nudges to suppliers.",
    bullets: [
      "One hub for supplier docs, job compliance, task certificates, toolbox talks and RAMS",
      "Statutory calendar per property — Gas Safety, EICR, fire risk assessment, LOLER, legionella, PAT",
      "Traffic-light expiry status; 30/14/7-day reminders, then an instant ping the moment anything lapses",
      "Overdue obligations raise the job automatically; the completed sheet files the new certificate",
      "One-click audit packs per property, supplier or portfolio — PDF or CSV — plus Slack / Teams alerts",
    ],
    mockup: "compliancehub",
    liveUrl: LIVE,
    stat: { value: "15 min", label: "alert sweep interval" },
    stats: [
      { value: "15m", label: "expiry sweep" },
      { value: "0", label: "lapsed certs on site" },
      { value: "1-click", label: "supplier re-upload" },
    ],
    pains: [
      { title: "You find out at the audit", desc: "The insurer's certificate expired in March. It's November. The job went ahead anyway." },
      { title: "Documents in six places", desc: "Supplier certs in email, job certs on a drive, task sign-offs on paper." },
      { title: "Chasing suppliers for PDFs", desc: "'Can you send your latest RAMS?' — the most-sent email in your organisation." },
      { title: "No single compliance number", desc: "Nobody can say what percentage of the estate — or the supplier base — is compliant right now." },
    ],
    deepDives: [
      { kicker: "New · statutory calendar", title: "Every obligation, per property, with a date on it.", body: "Each property carries its statutory duties — Gas Safety, EICR, fire risk assessment, LOLER, legionella, PAT, emergency lighting — with due dates driven by the last certificate. Overdue items raise a job automatically; the completed job sheet files the new certificate and rolls the date forward. One click exports the audit pack.", mockup: "compliancecalendar" },
      { kicker: "Deep dive", title: "You find out the moment it lapses — not at the audit.", body: "A dedicated alert scheduler sweeps every 15 minutes. The instant a document crosses its expiry date, every admin gets an email, an in-app notification and (if wired) a Slack or Teams ping — before the next job gets assigned to that supplier.", mockup: "compliancematrix" },
      { kicker: "How it works", title: "Suppliers fix gaps without logging in.", body: "Each expiry alert carries a one-click re-upload link. The supplier drops the new certificate, you approve it, and the traffic light goes green — with the old version kept for the audit trail. Toolbox talks and RAMS from each job file here too, so the safety record is in the same place as the certificates.", mockup: "webhooks" },
    ],
    steps: [
      { title: "Collect", desc: "Supplier docs on onboarding, job compliance on completion, task certificates from job sheets, toolbox talks and RAMS from each job." },
      { title: "Watch", desc: "Statutory calendar per property plus traffic-light status per document and supplier; sweeps every 15 minutes." },
      { title: "Alert", desc: "30/14/7-day reminders, then instant email, in-app and Slack/Teams on expiry — and an auto-raised job for overdue statutory work." },
      { title: "Block & prove", desc: "Non-compliant suppliers can't be assigned. One-click re-upload gets them green; one-click audit packs prove it." },
    ],
    faqs: [
      { q: "How is this different from Supplier Compliance?", a: "Supplier Compliance is the per-supplier document library and hard block. Compliance Hub is the single pane across suppliers, jobs, task certificates, toolbox talks and RAMS — with the statutory calendar, alert engine and webhooks." },
      { q: "Does it cover statutory property compliance, not just supplier documents?", a: "Yes — the statutory calendar tracks each property's obligations with due dates, auto-raises the job when something falls due, and files the resulting certificate." },
      { q: "Can we export an audit pack?", a: "Yes — per supplier, per property or portfolio-wide, as PDF or CSV, including toolbox talk and RAMS records." },
      { q: "Which document types are supported?", a: "Any — with templates for insurance, Gas Safe, NICEIC, RAMS, DBS, asbestos, LOLER, PAT, legionella and more." },
      { q: "Is Compliance Hub an add-on?", a: "Yes — £20/month, included in Professional and Enterprise." },
    ],
    quote: { text: "The compliance block has stopped two lapsed insurers before they hit site. That alone paid for the year.", name: "Daniel Reeve", role: "Procurement Lead, Capital Facilities" },
    related: ["supplier-compliance", "toolbox-talks", "ai-rams", "asset-tagging"],
  },
  {
    slug: "toolbox-talks",
    category: "management",
    flagship: true,
    isNew: true,
    icon: HardHat,
    name: "Toolbox Talks",
    kicker: "Safety briefings that leave a trail",
    headline: "Brief the crew. Capture the signatures. Keep the record.",
    summary: "Schedule toolbox talks from a library of topics or your own, deliver them on any phone before the shift, capture attendance and signatures, and file the record against the job, the site and each operative's training history — audit-ready, every time.",
    bullets: [
      "Library of ready-made talks (working at height, asbestos awareness, manual handling, hot works…) plus your own",
      "Attach a talk to a job, a site or a recurring contract so it's delivered before work starts",
      "Operatives read, acknowledge and sign on their phone — offline-safe",
      "Attendance, signatures, duration and the delivered version stored per operative",
      "Overdue talks and expiring refreshers surface in Compliance Hub and the 06:30 briefing",
    ],
    mockup: "toolbox",
    liveUrl: `${LIVE}/safety/toolbox-talks`,
    stat: { value: "40+", label: "ready-made topics" },
    stats: [
      { value: "40+", label: "ready-made topics" },
      { value: "e-sign", label: "per attendee, offline-safe" },
      { value: "3×", label: "filed: job · site · person" },
    ],
    pains: [
      { title: "A sign-in sheet in the van", desc: "Delivered, maybe. Signed, sometimes. Filed, never." },
      { title: "Same talk, no refresher", desc: "Nobody knows who last had the working-at-height talk, or when it lapses." },
      { title: "Suppliers are a black box", desc: "Your contractors brief their own crews — or don't — and you carry the liability." },
      { title: "The auditor asks for proof", desc: "Three years of talks in a drawer, half of them for people who've left." },
    ],
    deepDives: [
      { kicker: "Deep dive", title: "Delivered on the phone, before the first tool comes out.", body: "A talk attached to a job appears in the operative's Today's Snapshot. They read it, tick the acknowledgements, sign — and the job's 'Start' button unlocks. Supervisors can deliver to a group and collect every signature on one screen.", mockup: "talkdeliver" },
      { kicker: "How it works", title: "One record, three places.", body: "Each delivery is filed against the job pack, the site's safety file and every attendee's training record — with refresher intervals. Compliance Hub shows who is overdue, and the client can see the talk in their portal.", mockup: "compliancehub" },
    ],
    steps: [
      { title: "Pick", desc: "Choose from the library or write your own with sections, images and acknowledgements." },
      { title: "Attach", desc: "To a job, a site, a contract round or a date — with a refresher interval." },
      { title: "Deliver", desc: "Operatives or a supervisor run it on a phone; attendance and signatures captured." },
      { title: "File", desc: "Record stored per job, site and person; exportable PDF for audits and clients." },
    ],
    faqs: [
      { q: "Can suppliers deliver talks to their own crews?", a: "Yes — through Supplier Portal. You set which talks a job requires; their record is filed against your job and their compliance profile." },
      { q: "Does it work offline?", a: "Yes. Talks download with the day's jobs in the PWA; signatures sync when the signal returns." },
      { q: "Can we require a talk before a job starts?", a: "Yes — a required talk blocks 'Start job' until it's acknowledged and signed." },
      { q: "Is Toolbox Talks an add-on?", a: "Yes — a paid add-on. Pricing is being finalised; it's included in every trial today." },
    ],
    quote: { text: "Our HSE audit went from a two-day scramble to a filter and an export. Every talk, every signature, every refresher date.", name: "Daniel Reeve", role: "Procurement Lead, Capital Facilities" },
    related: ["ai-rams", "hs-library", "compliance-hub", "in-house"],
  },
  {
    slug: "hs-library",
    category: "management",
    flagship: true,
    isNew: true,
    icon: Library,
    name: "H&S Template Library",
    kicker: "Maintained by the Fixlr H&S team",
    headline: "Every H&S document you need. Copy it, make it yours, share it back.",
    summary: "Ready-made training courses, RAMS, risk assessments, method statements, toolbox talks, COSHH assessments, inspection checklists, permits and policies — UK-law-accurate and maintained by Fixlr's H&S team. Copy anything into your workspace as a draft, personalise it, save it in your portal and get told when a newer version is out. Written something good? Propose it to the library.",
    bullets: [
      "Hundreds of templates: training courses with quizzes, RAMS, risk assessments, toolbox talks, COSHH, inspection checklists, permits to work, fire risk & CDM documents, policies",
      "Copy into your workspace as a draft to personalise — your changes apply to your workspace only; reset restores the library default",
      "Versioned: every publish keeps a snapshot, so you see exactly what changed and when your copy is out of date",
      "Save and organise everything in your H&S portal; attach to jobs, assign training, publish policies and COSHH to clients' portals",
      "Propose your own documents to the library — the H&S team removes site, client and people details and publishes anonymously",
    ],
    mockup: "hslibrary",
    liveUrl: `${LIVE}/health-safety/library`,
    crossLink: { label: "Browse a sample of the library", to: "/library" },
    stat: { value: "£25", label: "per month, unlimited copies" },
    stats: [
      { value: "UK-law", label: "accurate, reviewed by the H&S team" },
      { value: "v1→vN", label: "snapshots & out-of-date alerts" },
      { value: "share", label: "propose yours · publish to clients" },
    ],
    pains: [
      { title: "Starting from a blank Word doc", desc: "Every new trade, hazard or client means another evening writing a RAMS or COSHH sheet from scratch." },
      { title: "Templates from 2017", desc: "Nobody knows if the risk assessment template still matches the regs — or who last changed it." },
      { title: "Ten versions in ten inboxes", desc: "The 'final' toolbox talk lives in a shared drive, a WhatsApp and three laptops." },
      { title: "Good work that never leaves the van", desc: "Your best checklist helps one crew. It could help every crew." },
    ],
    deepDives: [
      { kicker: "Deep dive", title: "Copy it. Make it yours. Keep it current.", body: "Pick a template, copy it into your workspace as a draft, edit every field in your own wording and branding, and save it into your portal's H&S folders. When the library publishes a newer version you're told what changed — compare and pull the update in, or keep your own. Your edits are never overwritten.", mockup: "hscopy" },
      { kicker: "Share it", title: "Propose your document to the library.", body: "Written a COSHH assessment the crew actually reads, or a checklist that caught a near miss? Propose it with an optional note. The platform's H&S team reviews it, removes your client, site and people details, and — if it's a good fit — publishes it anonymously for every workspace. You see how many times it's been copied.", mockup: "hsshare" },
    ],
    steps: [
      { title: "Browse", desc: "Filter by type, trade or hazard; 'New in the library' shows what the H&S team published this week." },
      { title: "Copy", desc: "One click copies the template into your workspace as a draft — never a shared file." },
      { title: "Personalise & save", desc: "Edit, brand, save to your portal's folders; attach to jobs, assign the course, issue the permit." },
      { title: "Share", desc: "Publish selected policies, COSHH and FRAs to clients' portals — and propose your best documents back to the library." },
    ],
    faqs: [
      { q: "Who writes and maintains the library?", a: "Fixlr's H&S team. Content is generic, UK-law-accurate and reviewed; every publish is versioned so you can compare your copy with the latest." },
      { q: "Can I edit the templates?", a: "Yes — every copy is a draft you personalise. Changes apply to your workspace only, 'reset' restores the library default, and new library versions never overwrite your edits." },
      { q: "What happens when I share a document?", a: "You propose it with an optional note to the platform team. They review it, strip site, client and people details, and publish it anonymously if it's a good fit. 'Your proposals to the library' shows the status and how often each one has been copied." },
      { q: "Can clients see my H&S documents?", a: "Yes — publish selected policies, COSHH assessments, fire risk assessments and CDM documents to a client's portal; their users are notified and always see the current version." },
      { q: "Is the H&S Template Library an add-on?", a: "Yes — £25/month, available on any plan and included in Enterprise." },
    ],
    quote: { text: "Our whole H&S pack went from a shared drive of mystery Word files to one library we actually trust — and the new starters' training courses came with it.", name: "Daniel Reeve", role: "Procurement Lead, Capital Facilities" },
    related: ["ai-rams", "toolbox-talks", "compliance-hub", "supplier-compliance"],
  },
  {
    slug: "staff-monitoring",
    category: "management",
    icon: Eye,
    name: "Staff Monitoring",
    kicker: "Team visibility",
    headline: "Know your team is working — without hovering.",
    summary: "Live presence, time-on-task, screenshots on a cadence you control and a weekly productivity report — for office and remote staff alike. Built for Operations Directors who need evidence, not assumptions, and configured with your team's consent in mind.",
    bullets: [
      "Live presence board: who's online, idle or offline right now",
      "Task timers tie hours to the jobs, quotes and requests they were spent on",
      "Optional periodic screenshots with configurable interval, blur and retention",
      "Weekly productivity report emailed to Operations Directors every Monday",
      "Role-gated: only Operations Directors see monitoring data; staff see their own",
      "Per-tenant settings — enable, tune or switch off for your whole company in one screen",
    ],
    mockup: "presence",
    liveUrl: LIVE,
    related: ["in-house", "reports", "scheduling"],
  },
  {
    slug: "white-label",
    category: "management",
    icon: Palette,
    name: "White-label & Multi-tenant",
    kicker: "For platforms",
    headline: "Resell Fixlr under your own brand.",
    summary: "Full tenant isolation. Custom domains (CNAME + TLS). Per-tenant logos, colours and email sender identities. A platform-admin layer for managing your downstream customers.",
    bullets: [
      "Custom domain per tenant with auto TLS (CNAME verification baked in)",
      "Per-tenant brand colours, logo, email sender",
      "Platform admin SaaS dashboard (MRR, churn, active tenants)",
      "Tenant-level isolation enforced at every DB query",
      "White-glove onboarding: bulk-invite suppliers, import clients",
    ],
    mockup: "branding",
    liveUrl: `${LIVE}/platform/branding`,
    related: ["supplier-network", "integrations", "client-portal"],
  },
  {
    slug: "integrations",
    category: "management",
    icon: Plug,
    name: "Integrations",
    kicker: "Plays nicely",
    headline: "Slack. Teams. Stripe. SendGrid. Twilio. Google Maps. AI.",
    summary: "Webhooks in, webhooks out. Generic, Slack Block Kit or Teams MessageCard. Stripe pay-links. SendGrid email. A universal LLM key for AI features — cost-controlled, centrally billed.",
    bullets: [
      "Tenant-level webhook config — Generic / Slack / Teams",
      "Daily morning brief to a Slack/Teams #operations channel at tenant-local 06:30",
      "Stripe payment links on invoices",
      "SendGrid + SMTP email fallback",
      "Google Maps for addresses + directions",
      "External REST API with tenant-level keys",
    ],
    mockup: "webhooks",
    liveUrl: LIVE,
    related: ["white-label", "emergency", "compliance-hub"],
  },
  {
    slug: "file-hub",
    category: "management",
    icon: FolderOpen,
    name: "File Hub & Knowledge Base",
    kicker: "Everything findable",
    headline: "Every photo, PDF and procedure, filed where it belongs.",
    summary: "Files attach to jobs, properties, assets, suppliers and contracts automatically. A knowledge base gives your operatives the procedures and your clients the answers.",
    bullets: [
      "Files auto-filed against jobs, properties, assets and suppliers",
      "Completion packs stitched from job sheets, photos and certificates",
      "Knowledge base articles with role-based visibility",
      "Cloud storage with local fallback and tenant-private buckets",
      "GDPR-ready export of everything you own",
    ],
    mockup: "files",
    liveUrl: LIVE,
    related: ["job-sheets", "assets", "client-portal"],
  },

  // ---------------- INTELLIGENCE ----------------
  {
    slug: "ai-supplier-matching",
    category: "intelligence",
    flagship: true,
    icon: Sparkles,
    name: "AI Supplier Matching",
    kicker: "Intelligence layer",
    headline: "Ask Fixlr: 'Who's the best plumber for this job?' — get an answer.",
    summary: "AI-powered supplier recommendations weighted by rating, response time, price history, compliance status and locality. Stop hand-picking suppliers for every job.",
    bullets: [
      "Weighted matching across 5+ criteria (rating, avg quote, SLA hit rate, distance, compliance)",
      "One-click assign of the top suggestion",
      "Reasoning surfaced — 'Why this supplier?' on every suggestion",
      "Learns from your accept/reject feedback over time",
      "Suggested reassignments when the current pick is overloaded",
    ],
    mockup: "aimatch",
    liveUrl: LIVE,
    stat: { value: "<1s", label: "to rank your whole supplier panel" },
    stats: [
      { value: "5+", label: "weighted signals" },
      { value: "<1s", label: "to rank the panel" },
      { value: "1-click", label: "assign the top pick" },
    ],
    pains: [
      { title: "Gut-feel dispatch", desc: "The same two suppliers get every job because they're the names you remember." },
      { title: "Distance is a guess", desc: "You send the supplier in Croydon to Camden because you didn't check." },
      { title: "Lapsed certs slip through", desc: "The best-rated supplier's insurance expired last week and nobody looked." },
      { title: "Overloaded favourites", desc: "One supplier has eleven open jobs; another has none. The client waits." },
    ],
    deepDives: [
      { kicker: "Deep dive", title: "Every score, explained.", body: "Each suggestion shows its weighted breakdown — distance, 90-day rating, SLA hit-rate, price history, compliance — so a coordinator can override with confidence, and every override teaches the model.", mockup: "aimatch" },
      { kicker: "Suggested reassignments", title: "When one supplier is drowning and another is idle.", body: "Fixlr flags the imbalance on your dashboard with a 'Reassign?' suggestion, ranked by the same signals, and moves the job in one click if you agree.", mockup: "reassign" },
    ],
    steps: [
      { title: "Read the job", desc: "Trade, property location, urgency, SLA and any client-specific supplier rules." },
      { title: "Score the panel", desc: "Every compliant supplier is scored across rating, response time, price history and distance." },
      { title: "Explain", desc: "Top three shown with 'Why this supplier?' reasoning and the weighted breakdown." },
      { title: "Assign & learn", desc: "One click to assign. Accept/reject feedback tunes the weights for your tenant." },
    ],
    faqs: [
      { q: "Can we tune the weights?", a: "Yes — per tenant, and per trade category. Some teams weight distance highest for emergencies and price for planned work." },
      { q: "Does it ever suggest a non-compliant supplier?", a: "No. Compliance is a hard filter before scoring, not a factor in it." },
      { q: "Is this a chatbot?", a: "No. It's a ranked decision surface inside the assign picker. The separate AI Assistant handles natural-language questions about your live data." },
      { q: "Which plan includes it?", a: "AI Supplier Matching ships with the AI Drafting & Assistant add-on (£15/month), included in Professional and Enterprise." },
    ],
    quote: { text: "It picked a supplier we'd never have chosen — closer, cheaper, fully compliant. Now we just check the reasoning and click.", name: "Chris H.", role: "Head of Operations, Belmont Estates" },
    related: ["supplier-network", "supplier-portal", "emergency"],
  },
  {
    slug: "ai-assistant",
    category: "intelligence",
    icon: Wand2,
    name: "AI Drafting & Assistant",
    kicker: "Grounded in your live data",
    headline: "Scopes, caveats and notices written for you. Answers from your own numbers.",
    summary: "AI-written scopes of work, caveats and resident notices — plus an in-app assistant that can see your tenant's live jobs, emergencies and compliance documents.",
    bullets: [
      "Draft a scope of works from a one-line prompt",
      "Caveats, exclusions and notices in your house style",
      "'What needs my attention today?' answered from live tenant data",
      "'Any compliance docs expiring soon?' with links to fix them",
      "Your data is never used to train external models",
    ],
    mockup: "aidraft",
    liveUrl: LIVE,
    related: ["ai-supplier-matching", "quotes", "compliance-hub"],
  },
  {
    slug: "ai-rams",
    category: "intelligence",
    flagship: true,
    isNew: true,
    icon: FileWarning,
    name: "AI RAMS",
    kicker: "RAMS, drafted from the job",
    headline: "A RAMS pack in a minute — from the job you already raised.",
    summary: "Fixlr reads the job — trade, tasks, property, asset, access, live hazards from previous visits — and drafts a complete Risk Assessment & Method Statement in your house template. Review, tweak, approve; the operative signs it on site and it files into the job pack.",
    bullets: [
      "Hazards, who's at risk, controls, residual risk and method steps generated from the job scope",
      "Pulls site-specific facts: asbestos register, access constraints, previous incidents, asset manuals",
      "Your templates, your wording — AI fills them, a competent person approves",
      "Version history, approver sign-off and operative acknowledgement on site",
      "Shared with the client and supplier portals; refreshed when the scope changes",
    ],
    mockup: "rams",
    demo: "rams",
    liveUrl: `${LIVE}/jobs/JOB-91AD22C0/rams`,
    stat: { value: "<60s", label: "to a full RAMS draft" },
    stats: [
      { value: "<60s", label: "to a full draft" },
      { value: "site-aware", label: "asbestos, access, history" },
      { value: "v1→vN", label: "versioned & signed" },
    ],
    pains: [
      { title: "Copy-paste RAMS", desc: "Last year's document with a new address at the top — and the same hazards it missed then." },
      { title: "Written at 10pm the night before", desc: "The job needs a RAMS; the supervisor writes one from memory." },
      { title: "Never reaches the person doing the work", desc: "The RAMS lives in an email; the operative on site has never seen it." },
      { title: "Clients want it before they'll let you in", desc: "Access blocked while someone finds the right file." },
    ],
    deepDives: [
      { kicker: "Deep dive", title: "It knows the site, not just the trade.", body: "The draft isn't generic. Fixlr pulls the property's asbestos register, access notes, working-at-height flags, the asset's manual and anything logged on previous visits — so the hazards and controls are the ones that matter on this job.", mockup: "ramsbuilder" },
      { kicker: "How it works", title: "Reviewed, approved, signed on site.", body: "The supervisor edits and approves; the operative reads and signs on the phone before 'Start job' unlocks. The signed PDF files into the job pack and the client portal. Change the scope and Fixlr proposes a new version with the differences highlighted.", mockup: "jobsheet" },
    ],
    steps: [
      { title: "Raise the job", desc: "Scope, trade, property and asset as normal." },
      { title: "Generate", desc: "One click drafts the RAMS from the job and the site's known hazards, in your template." },
      { title: "Review & approve", desc: "Edit anything, add a control, approve — version 1 is locked and timestamped." },
      { title: "Sign & file", desc: "Operative acknowledges on site; PDF lands in the job pack, client and supplier portals." },
    ],
    faqs: [
      { q: "Is the RAMS legally compliant?", a: "It's a complete draft in your approved template, built from your controls library; a competent person reviews and approves it, exactly as with a manually written RAMS. Fixlr records who approved what, when." },
      { q: "Can suppliers use it?", a: "Yes — suppliers can generate a RAMS for the job they've accepted from Supplier Portal, and you approve it before they start." },
      { q: "Where does the hazard knowledge come from?", a: "Your own controls library and templates, plus site data already in Fixlr (asbestos register, access notes, incidents, asset manuals). Your data is never used to train external models." },
      { q: "Is AI RAMS an add-on?", a: "Yes — a paid add-on in the Intelligence lane. Pricing is being finalised; it's included in every trial today." },
    ],
    quote: { text: "We used to lose a day a week to RAMS paperwork. Now the draft is waiting when the job is raised and the engineer signs it on the doorstep.", name: "Amira K.", role: "Commercial Manager, Northgate FM" },
    related: ["toolbox-talks", "hs-library", "compliance-hub", "jobs"],
  },
  {
    slug: "advanced-analytics",
    category: "intelligence",
    icon: LineChart,
    name: "Advanced Analytics",
    kicker: "Beyond the dashboard",
    headline: "P&L by client. Scorecards by supplier. Trends by trade.",
    summary: "Profit & loss, client spend, supplier scorecards and tenant analytics dashboards — the numbers a board asks for, without a spreadsheet weekend.",
    bullets: [
      "Profit & loss per job, client, contract and property",
      "Supplier scorecards: quality, speed, price, compliance",
      "Client spend and margin trends",
      "Operative utilisation and first-time-fix by trade",
      "Scheduled exports to PDF / CSV and webhook events",
    ],
    mockup: "reports",
    liveUrl: LIVE,
    related: ["reports", "invoicing", "supplier-compliance"],
  },
];

export const MODULES = BASE_MODULES.map((m) => ({ ...(DEEP[m.slug] || {}), ...m, shot: SHOTS[m.slug] ? `/shots/${SHOTS[m.slug]}.png` : null }));
export const FLAGSHIPS = MODULES.filter((m) => m.flagship);
export const getModule = (slug) => MODULES.find((m) => m.slug === slug);
export const getCategory = (slug) => CATEGORIES.find((c) => c.slug === slug);
export const modulesIn = (categorySlug) => MODULES.filter((m) => m.category === categorySlug);
export const moduleHref = (m) => `/product/${m.slug}`;
